import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolder,
  faFolderOpen,
  faFile,
  faSearch,  // [NEW] Added icon for search input decoration
} from "@fortawesome/free-solid-svg-icons";
import AllTreeContext from "./AllTreeContext";
import "../styles/LeftNavigation.css";

const treeSource = "all-tree";

const renderTree = (
  nodes,
  expandedNodes,
  toggleNode,
  handleDragStart,
  handleContextMenu,
  handleNodeClick,
  isMain, // [NEW] Added isMain prop to determine if it's the main tree
  showSearchBar,  // [NEW] Added showSearchBar prop
  searchTerm, // [NEW] Added searchTerm prop
  handleSearch  // [NEW] Added handleSearch prop
) => {
  return (
    <ul style={{ listStyleType: "none", paddingLeft: "14px" }}>
      {nodes.length === 0 && isMain ? (
        <>
        {/* [UPDATED] Styled search input with icon when there are no matched items to keep the UI consistent */}
          {showSearchBar && (
            <div className="relative my-2">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              </div>
              <input
                className=" pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-black focus:outline-none"
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => handleSearch(e)}
              />
            </div>
          )}
          <li className="text-muted px-3">No items found</li>
        </>
      ) : (
        nodes.map((node) => (
          <li
            key={node.id}
            draggable
            onDragStart={(e) => handleDragStart(e, node)}
            onContextMenu={(e) => handleContextMenu(e, node)}
            className={`${!isMain && "py-2" }`}
          >
            {node.type === "folder" ? (
              <>
                <span
                  onClick={() => toggleNode(node.id)}
                  style={{ cursor: "pointer" }}
                  title={node.id}
                >
                  <FontAwesomeIcon
                    icon={
                      expandedNodes.includes(node.id) ? faFolderOpen : faFolder
                    }
                    className="pr-2"
                  />
                  {node.label}
                </span>

                {expandedNodes.includes(node.id) && node.children &&
                node.children.length > 0 &&  (
                  <div>
                    {showSearchBar && isMain && (
                      <div className="relative my-2">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <FontAwesomeIcon
                            icon={faSearch}
                            className="text-gray-400"
                          />
                        </div>
                        <input
                          className=" pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-black focus:outline-none"
                          type="text"
                          placeholder="Search..."
                          value={searchTerm}
                          onChange={(e) => handleSearch(e)}
                        />
                      </div>
                    )}

                    {node.children && node.children.length > 0 ? (
                      renderTree(
                        node.children,
                        expandedNodes,
                        toggleNode,
                        handleDragStart,
                        handleContextMenu,
                        handleNodeClick,
                        false,  // [NEW] Set isMain to false
                        false,  // [NEW] Set showSearchBar to false
                        searchTerm,  // [NEW] Pass searchTerm
                        handleSearch  // [NEW] Pass handleSearch
                      )
                    ) : searchTerm ? (
                      // [UPDATED] Styled search input with icon when there are no matched items to keep the UI consistent
                      <div className="text-muted px-3">No items found</div>
                    ) : null}
                  </div>
                )}
              </>
            ) : (
              <span
                onClick={() => handleNodeClick(node)}
                style={{ cursor: "pointer" }}
                title={`[${node.instance_type}] ${node.label}`}
              >
                <FontAwesomeIcon icon={faFile} /> {node.label}
              </span>
            )}
          </li>
        ))
      )}
    </ul>
  );
};

const AllTreeView = ({
  nodes,
  showSearchBar = false,
  onAddToMyDashboards,
  setCurrentPage,
  setSelectedNodeId,
}) => {
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [filteredNodes, setFilteredNodes] = useState(nodes);
  const [searchTerm, setSearchTerm] = useState("");
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    node: null,
  });
  const contextMenuRef = useRef(null);

  useEffect(() => {
    setFilteredNodes(nodes);
  }, [nodes]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target)
      ) {
        setContextMenu({ visible: false, x: 0, y: 0, node: null });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [contextMenuRef]);

  const toggleNode = (nodeId) => {
    setExpandedNodes((prevExpandedNodes) =>
      prevExpandedNodes.includes(nodeId)
        ? prevExpandedNodes.filter((id) => id !== nodeId)
        : [...prevExpandedNodes, nodeId]
    );
  };

  const handleDragStart = (e, node) => {
    e.stopPropagation();
    if (node.type === "dashboard") {
      e.dataTransfer.setData(
        "application/json",
        JSON.stringify({ ...node, treeSource, folderId: node.parent_id })
      );
    }
  };

  const handleContextMenu = (e, node) => {
    e.preventDefault();
    if (node.type === "dashboard") {
      setContextMenu({ visible: true, x: e.clientX, y: e.clientY, node });
    }
  };

  const handleNodeClick = (node) => {
    if (node.type === "dashboard") {
      setSelectedNodeId(node.id);
      setCurrentPage("dashboard");
    }
  };

  const handleAddToMyDashboards = () => {
    onAddToMyDashboards(contextMenu.node);
    setContextMenu({ visible: false, x: 0, y: 0, node: null });
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    if (value === "") {
      setFilteredNodes(nodes);
    } else {
      const filterTree = (tree) => {
        return tree
          .map((node) => {
            if (node.children) {
              const filteredChildren = filterTree(node.children);
              if (
                node.label.toLowerCase().includes(value) ||
                filteredChildren.length > 0
              ) {
                return { ...node, children: filteredChildren };
              }
            } else if (node.label.toLowerCase().includes(value)) {
              return node;
            }
            return null;
          })
          .filter((node) => node !== null);
      };
      const result = filterTree(nodes);
      setFilteredNodes(result.length > 0 ? result : []);
    }
  };

  const isMain = true;  // [NEW] Set isMain to true to manage the main root to add search fot it only

  return (
    <div>
      {renderTree(
        filteredNodes,
        expandedNodes,
        toggleNode,
        handleDragStart,
        handleContextMenu,
        handleNodeClick,
        isMain,  // [NEW] Pass isMain
        showSearchBar,  // [NEW] Pass showSearchBar
        searchTerm,  // [NEW] Pass searchTerm
        handleSearch  // [NEW] Pass handleSearch
      )}

      {contextMenu.visible && (
        <div ref={contextMenuRef}>
          <AllTreeContext
            x={contextMenu.x}
            y={contextMenu.y}
            onAddToMyDashboards={handleAddToMyDashboards}
          />
        </div>
      )}
    </div>
  );
};

export default AllTreeView;
